import { EyeIcon, PlayIcon, VolumeUpIcon } from "@heroicons/react/outline";
import { useEffect, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/solid";

const VideoGallery = ({ data }) => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);


  const handleRightClick = (e) => {
    e.preventDefault();
    setShowWarning(true);
  }

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!selectedVideo) return;

      if (event.key === "ArrowLeft") {
        navigateVideo(-1);
      } else if (event.key === "ArrowRight") {
        navigateVideo(1);
      } else if (event.key === "Escape") {
        closeModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedVideo]);

  const openModal = (videoData, timeFrame, sectionVideos) => {
    const largeVideoUrl = `/videos/original/${String(videoData.id).padStart(2, "0")}.mp4`;
    setSelectedVideo({
      ...videoData,
      url: largeVideoUrl,
      timeFrame: timeFrame,
      sectionVideos: sectionVideos,
    });
    setIsLoading(true);
  };

  const closeModal = () => {
    setSelectedVideo(null);
    setIsLoading(false);
  };

  const handleVideoLoad = () => {
    setIsLoading(false);
  };

  const navigateVideo = (direction) => {
    const currentIndex = selectedVideo.sectionVideos.findIndex(
      (video) => video.id === selectedVideo.id
    );

    let newIndex = currentIndex + direction;

    if (newIndex >= selectedVideo.sectionVideos.length) {
      newIndex = 0;
    } else if (newIndex < 0) {
      newIndex = selectedVideo.sectionVideos.length - 1;
    }

    const nextVideo = selectedVideo.sectionVideos[newIndex];
    const largeVideoUrl = `/videos/original/${String(nextVideo.id).padStart(2, "0")}.mp4`;

    setSelectedVideo({
      ...nextVideo,
      url: largeVideoUrl,
      timeFrame: selectedVideo.timeFrame,
      sectionVideos: selectedVideo.sectionVideos,
    });

    setIsLoading(true);
  };

  return (
    <div className="px-6 min-h-[70vh]">
      {data.map((section) => (
        <>
          <h2 className="text-md lg:text-lg font-extralight h-10 sticky top-0 z-10 bg-white">
            {section?.timeFrame}
          </h2>
          <div className="grid lg:grid-cols-4 md:grid-cols-4 sm:grid-cols-2 grid-cols-2 gap-8">
            {section.videos.map((item) => (
              <div key={item.id} className="relative group text-center">
                <img
                  src={`/videos/thumbnails/${String(item.id).padStart(2, "0")}.jpg`}
                  alt={item.title}
                  className="w-full h-auto rounded-md border border-gray-300 cursor-pointer"
                />
                <div
                  onClick={() =>
                    openModal(item, section?.timeFrame, section.videos)
                  }
                  className="absolute cursor-pointer inset-0 flex items-center justify-center bg-black bg-opacity-65 opacity-0 group-hover:opacity-100 rounded-md"
                >
                  <p className="text-white font-extralight text-lg flex items-center gap-1">
                    <PlayIcon className="h-5 w-5" /> <span>Play</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </>
      ))}

      {selectedVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
          onClick={closeModal}
        >
          <div
            className="relative bg-white max-w-full max-h-[99vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-0 right-0 text-[10px] m-2 bg-gray-200 hover:bg-red-500 w-6 h-6 rounded-full text-gray-500 hover:text-white"
              onClick={closeModal}
            >
              ✕
            </button>

            <div className="flex flex-col justify-center items-center overflow-hidden p-10 pb-0">
              {isLoading && (
                <div className="absolute inset-0 flex justify-center items-center bg-black bg-opacity-50">
                  <div className="w-16 h-16 border-t-4 border-b-4 border-white rounded-full animate-spin"></div>
                </div>
              )}

              <video
                src={selectedVideo.url}
                autoPlay
                className="max-w-[90vw] max-h-[70vh] object-contain"
                onLoadedData={handleVideoLoad}
                controls
                onContextMenu={(e) => handleRightClick(e)
                }
                onError={(e) => {
                  e.target.style.display = "none";
                }}
                controlsList="nodownload"
                disablePictureInPicture
              >
                Your browser does not support the video tag.
              </video>


              {
                showWarning && (
                  <div className="bg-gray-700" style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'white',
                    padding: '6px 15px',
                    zIndex: 1000,
                    fontSize: '10px'
                  }}>
                    <div className="flex justify-between mb-3">
                      <h3 className="font-bold text-md">
                        WARNING
                      </h3>
                      <button
                        onClick={() => setShowWarning(false)}
                        style={{
                          marginLeft: '10px',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: 'bolder',
                        }}
                      >
                        X
                      </button>
                    </div>
                    <span>
                      All content is protected by copyright and may not be <br /> downloaded or copied.
                    </span>

                  </div>
                )
              }

             
              <div className="text-center mx-5 max-w-[400px] flex flex-col justify-center items-center mt-3">
                <h2 className="text-md font-bold mb-2">{selectedVideo.title}</h2>
                <p className="text-[13px] text-gray-600 mb-[2px]">
                  {selectedVideo.description}
                </p>
                <p className="text-[13px] text-gray-600 mb-[2px]">
                  {selectedVideo.timeFrame}
                </p>
                <p className="text-[13px] text-gray-600">
                  {selectedVideo.status}
                </p>
              </div>
            </div>
            <div className="flex z-10 justify-end p-4 pt-0">
                <button
                  onClick={() => navigateVideo(-1)}
                  className="flex items-center p-1 rounded-full"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>

                <button
                  onClick={() => navigateVideo(1)}
                  className="flex items-center p-1 rounded-full"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoGallery;
